"use client";
import React, { useState, useMemo } from "react";
import { cva } from "class-variance-authority";
import { CaseCard } from "@/components/ui/cards/caseCard";
import {
  AnimatedCard,
  AnimatedCardContainer,
  TiltWrapper,
} from "@/components/animations";
import { Button } from "@/components/ui/buttons";
import type {
  CaseCardColor,
  CaseCardItem,
  ImageProps,
  ThemeVariant,
} from "@/types";
import { ChipFilterGroup } from "../ui/ChipFilterGroup";
import { useGlobalTagFilter } from "@/lib/hooks/useGlobalTagFilter";
import { useTranslations } from "@/lib/i18n/useTranslations";

const caseCardTeaserVariants = cva("container py-lg", {
  variants: {
    variant: {
      light: "",
      dark: "text-white",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface CaseCardTeaserProps {
  variant?: ThemeVariant;
  cards: CaseCardItem[];
  className?: string;
  showFilter?: boolean;
  /** Options for "Load More" functionality */
  loadMore?: {
    /** Initial number of items to show */
    initialCount: number;
    /** Number of items to add on each click */
    step: number;
    /** Label for the button */
    buttonLabel?: string;
  };
}

/**
 * CaseCardTeaser block component that displays case cards in an alternating grid layout
 *
 * Cards are displayed in a 3-column grid with alternating sizes:
 * - Small cards: 1 column
 * - Large cards: 2 columns
 */
export const CaseCardTeaser: React.FC<CaseCardTeaserProps> = ({
  cards,
  variant = "light",
  className = "",
  showFilter = false,
  loadMore,
}) => {
  const {
    filteredItems: filteredCards,
    allTags,
    setSelectedTags,
  } = useGlobalTagFilter(cards);
  const [visibleCount, setVisibleCount] = useState(
    loadMore?.initialCount ?? cards.length
  );

  const t = useTranslations();

  const displayedCards = useMemo(
    () => filteredCards.slice(0, visibleCount),
    [filteredCards, visibleCount]
  );

  const hasMore = visibleCount < filteredCards.length;

  const handleLoadMore = () => {
    if (loadMore) {
      setVisibleCount((prev) =>
        Math.min(prev + loadMore.step, filteredCards.length)
      );
    }
  };

  function getDisplayColorAndImage(
    card: CaseCardItem,
    index: number
  ): { color?: CaseCardColor; image?: ImageProps } {
    const { color, image } = card;
    const isFirst = index === 0;
    const isLast = index === cards.length - 1;
    const isFirstOrLast = isFirst || isLast;

    // any card that is not first or last stays as is
    if (!isFirstOrLast) return { color, image };
    // when there are only two cards, the last one should either have an image or a different color
    if (cards.length === 2 && isLast)
      return { image, color: variant === "light" ? "purple" : "yellow" };
    // when there are more than two cards, the first and last one should be the color fitting the variant
    return { color: variant === "light" ? "yellow" : "purple" };
  }

  return (
    <div className={caseCardTeaserVariants({ variant, className })}>
      {showFilter && allTags.length > 0 && (
        <ChipFilterGroup
          variant={variant ?? "light"}
          label={t.common.selectCategory}
          tags={allTags}
          canSelectNone
          searchParamName="case-filter"
          onChange={(tags) => {
            setSelectedTags(tags);
            setVisibleCount(loadMore?.initialCount ?? cards.length);
          }}
        />
      )}

      <div className="col-span-full grid grid-cols-12 auto-rows-fr gap-md">
        <AnimatedCardContainer>
          {displayedCards.map((card, index) => {
            const isLarge = index % 4 === 1 || index % 4 === 2;
            const colSpanClass = isLarge
              ? "col-span-full md:col-span-6 lg:col-span-8"
              : "col-span-full md:col-span-6 lg:col-span-4";

            const { color: displayColor, image: displayImage } =
              getDisplayColorAndImage(card, index);
            return (
              <AnimatedCard
                className={colSpanClass}
                key={`service-card-slide-${card.id}`}
                id={card.id}
              >
                <TiltWrapper
                  key={index}
                  className={colSpanClass + " size-full"}
                  rotateAmplitude={card.size === "lg" ? 5 : 10}
                >
                  <CaseCard
                    key={card.id || index}
                    {...card}
                    size={isLarge ? "lg" : "sm"}
                    color={displayColor}
                    image={displayImage}
                    className={colSpanClass}
                  />
                </TiltWrapper>
              </AnimatedCard>
            );
          })}
        </AnimatedCardContainer>
      </div>

      {loadMore && hasMore && (
        <div className="col-span-full flex justify-center mt-xl">
          <Button
            variant="secondary"
            color={variant === "light" ? "yellow" : "purple"}
            onClick={handleLoadMore}
            label={loadMore.buttonLabel || "Mehr laden"}
            icon="arrow-right"
          />
        </div>
      )}
    </div>
  );
};
